// The correct clock hands (hour, minute endpoints) computed from the target time, for the reveal.
// Order corners around their centroid so the revealed polygon draws as the real figure rather than a
// self-crossing bowtie. The labels (3rd element) ride along untouched. Convex-safe, which covers the
// figures this is used for (parallelogram / quadrilateral from given + plotted corners).
const orderClosedBoundary = (points) => {
  const pts = (points || []).filter((p) => Array.isArray(p) && p.length >= 2);
  if (pts.length < 3) return pts;
  const cx = pts.reduce((s, p) => s + Number(p[0]), 0) / pts.length;
  const cy = pts.reduce((s, p) => s + Number(p[1]), 0) / pts.length;
  return [...pts].sort(
    (a, b) => Math.atan2(Number(a[1]) - cy, Number(a[0]) - cx) - Math.atan2(Number(b[1]) - cy, Number(b[0]) - cx)
  );
};

const angleRevealArms = (spec) => {
  const pt = (p) => (Array.isArray(p) && p.length >= 2 && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1]))
    ? [Number(p[0]), Number(p[1])]
    : null);
  const v = pt(spec.vertex);
  const target = Number(spec.target_angle);
  if (!v || !Number.isFinite(target)) return null;
  const given = pt(spec.given_ray);
  const r = 0.3;
  // Direction of the printed arm, or due east when there is none and both arms are the student's.
  // Fractional x/y are not isotropic — swing the arm in aspect-corrected space so the revealed
  // angle is the one gradeInteractiveAngle measures. Mirrors its `aspect` handling.
  const aspect = Number(spec.aspect) > 0 ? Number(spec.aspect) : 1;
  const base = given ? Math.atan2(-(given[1] - v[1]), (given[0] - v[0]) * aspect) : 0;
  const at = (rad) => [v[0] + (r * Math.cos(rad)) / aspect, v[1] - r * Math.sin(rad)];
  const swung = at(base + (target * Math.PI) / 180);
  // With a printed arm only the swung one is the student's; with none, show both.
  return { vertex: v, rays: given ? [swung] : [at(base), swung] };
};

const clockRevealHands = (spec) => {
  const c = Array.isArray(spec.center) && spec.center.length >= 2 ? spec.center : [0.5, 0.5];
  const r = Number(spec.radius) || 0.4;
  const hour = (Number(spec.target_hour) || 0) % 12, minute = Number(spec.target_minute) || 0;
  const hb = hour * 30 + minute * 0.5, mb = minute * 6;
  const ep = (bearing, rr) => [c[0] + Math.sin((bearing * Math.PI) / 180) * rr, c[1] - Math.cos((bearing * Math.PI) / 180) * rr];
  return [ep(hb, r * 0.55), ep(mb, r * 0.92)];
};

// One valid example for a shade "metrics" question: scan for a w×h rectangle of grid cells whose
// area (w*h) and perimeter (2*(w+h)) match the targets, then map its cells to region ids. A
// rectangle exists for every (area, perimeter) the exam poses here; if none fits, returns [].
// When target_shape_count > 1 ("draw TWO rectangles, each with perimeter 18") lay one rectangle per
// required shape side by side with a one-column gap, honouring distinct_side_lengths.
const metricsRevealExample = (spec) => {
  const byCell = new Map((spec.regions || []).map((r) => [`${r.cell?.[0]},${r.cell?.[1]}`, String(r.id)]));
  const cols = (spec.regions || []).reduce((m, r) => Math.max(m, (r.cell?.[0] ?? 0) + 1), 0);
  const rows = (spec.regions || []).reduce((m, r) => Math.max(m, (r.cell?.[1] ?? 0) + 1), 0);
  const wantArea = spec.target_area, wantPerim = spec.target_perimeter;
  const dims = [];
  for (let h = 1; h <= rows; h++) {
    for (let w = 1; w <= cols; w++) {
      if (wantArea != null && w * h !== wantArea) continue;
      if (wantPerim != null && 2 * (w + h) !== wantPerim) continue;
      dims.push([w, h]);
    }
  }
  if (!dims.length) return [];

  const need = Math.max(1, Math.round(Number(spec.target_shape_count) || 1));
  const distinct = !!spec.distinct_side_lengths;
  const chosen = [];
  const usedSides = new Set();
  for (const [w, h] of dims) {
    if (chosen.length >= need) break;
    if (distinct && (usedSides.has(w) || usedSides.has(h))) continue;
    chosen.push([w, h]);
    usedSides.add(w);
    usedSides.add(h);
  }
  if (chosen.length < need) return [];

  // Shelf packing: fill the row left to right, and drop to a new shelf below when the next rectangle
  // no longer fits. Laying them out side by side only worked when the widths happened to fit on one
  // row — "two rectangles of perimeter 18" on a 15x10 grid gives 8x1 and 7x2, which do not, so the
  // whole reveal was abandoned and the "Correct solution" showed an empty grid.
  const ids = [];
  let col = 0, row = 0, shelfH = 0;
  for (const [w, h] of chosen) {
    if (col + w > cols) {
      row += shelfH + 1;
      col = 0;
      shelfH = 0;
    }
    if (col + w > cols || row + h > rows) return [];
    for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) ids.push(byCell.get(`${col + c},${row + r}`));
    col += w + 1;
    shelfH = Math.max(shelfH, h);
  }
  return ids.every(Boolean) ? ids : [];
};

// For a polygon "sketch N rectangles by area" question, lay one example rectangle per target area on
// the grid (h = smallest divisor of the area keeping w ≤ cols), stacked vertically with a 1-cell gap.
// Returns a flat corner list (4 pts per rectangle) the canvas groups into quads via shape_count.
const multiRectReveal = (spec) => {
  const areas = Array.isArray(spec.target_areas) ? spec.target_areas.map(Number).filter((n) => n > 0) : [];
  if (!areas.length) return [];
  const cols = Math.max(1, Math.round(Number(spec.grid?.cols) || 8));
  const rows = Math.max(1, Math.round(Number(spec.grid?.rows) || 8));
  const dims = areas.map((a) => {
    for (let h = 1; h <= rows; h++) {
      if (a % h === 0 && a / h <= cols) return [a / h, h];
    }
    return null;
  });
  if (dims.some((d) => !d)) return [];
  const totalH = dims.reduce((s, [, h]) => s + h, 0) + (dims.length - 1);
  if (totalH > rows) return [];
  const pts = [];
  let y = 0;
  for (const [w, h] of dims) {
    pts.push([0, y], [w, y], [w, y + h], [0, y + h]);
    y += h + 1;
  }
  return pts;
};

// Admin edge list ([[ax,ay],[bx,by]], {from,to}, or "gx,gy|gx,gy") → canvas edge keys.
const edgeKeysFrom = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
  const edgeKey = (a, b) => [cornerKey(a), cornerKey(b)].sort().join("|");
  const asPoint = (entry) => (
    Array.isArray(entry) && entry.length >= 2 && Number.isFinite(Number(entry[0])) && Number.isFinite(Number(entry[1]))
      ? [Number(entry[0]), Number(entry[1])]
      : null
  );
  const keys = [];
  for (const pair of raw) {
    if (typeof pair === "string" && pair.includes("|")) {
      keys.push(pair);
      continue;
    }
    if (Array.isArray(pair) && pair.length >= 2) {
      const a = asPoint(pair[0]);
      const b = asPoint(pair[1]);
      if (a && b) keys.push(edgeKey(a, b));
      continue;
    }
    if (pair && typeof pair === "object") {
      const a = asPoint(pair.from);
      const b = asPoint(pair.to);
      if (a && b) keys.push(edgeKey(a, b));
    }
  }
  return keys;
};

const pointsWithEdgeVertices = (points, edgeKeys) => {
  const seen = new Set((points || []).map((p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`));
  const out = [...(points || [])];
  for (const key of edgeKeys) {
    for (const part of String(key).split("|")) {
      if (!part || seen.has(part)) continue;
      const [gx, gy] = part.split(",").map(Number);
      if (!Number.isFinite(gx) || !Number.isFinite(gy)) continue;
      seen.add(part);
      out.push([gx, gy]);
    }
  }
  return out;
};

// Derives the reveal value (correct answer to draw) for an InteractiveDiagramCanvas from a full
// interactive spec (with target_* fields). Shared by take, review and result pages.
export const interactiveRevealValue = (spec) => {
  if (!spec) return null;
  // A construction's "correct shape" is the model the author drew on the board (with the measured
  // readings filled in), reachable only through the full spec the review payload carries.
  if (spec.kind === "construction") {
    const model = spec.model && typeof spec.model === "object" ? spec.model : null;
    // A measuring part ("Measure the line AB") has no model drawing — its answer is the reading, so
    // fill the reading boxes from the authored measures; the box was left empty under "Correct
    // solution" (Kent College 2007 Q8a).
    const readings = {};
    (Array.isArray(spec.measures) ? spec.measures : []).forEach((m) => {
      if (m && String(m.label || "").trim() && m.value != null && String(m.value).trim() !== "") {
        readings[String(m.label).trim()] = String(m.value);
      }
    });
    const measures = { ...readings, ...(model?.measures && typeof model.measures === "object" ? model.measures : {}) };
    if (!model && Object.keys(measures).length === 0) return null;
    return Object.keys(measures).length ? { ...(model || {}), measures } : model;
  }
  if (spec.kind === "polygon" && Array.isArray(spec.target_areas) && spec.target_areas.length) {
    return multiRectReveal(spec);
  }
  if (spec.kind === "shade") {
    const ids = shadeRevealIds(spec);
    // "…and draw on the line of symmetry": the mirror line rides alongside the shaded ids.
    if (Array.isArray(spec.lines?.target_lines) && spec.lines.target_lines.length) {
      return [...ids, ...spec.lines.target_lines.map((l) => ({ line: l }))];
    }
    return ids;
  }
  return revealValueForKind(spec);
};

const shadePolyominoRevealIds = (spec) => {
  const shape = Array.isArray(spec.target_shape) ? spec.target_shape : [];
  const regions = Array.isArray(spec.regions) ? spec.regions : [];
  const cells = shape
    .filter((c) => Array.isArray(c) && c.length >= 2)
    .map((c) => [Number(c[0]), Number(c[1])]);
  if (!cells.length || !regions.length) return [];
  const minCol = Math.min(...cells.map((c) => c[0]));
  const minRow = Math.min(...cells.map((c) => c[1]));
  const relative = cells.map(([col, row]) => [col - minCol, row - minRow]);
  const byCell = new Map();
  const byGrid = new Map();
  for (const rg of regions) {
    if (rg.id == null || !Array.isArray(rg.cell) || rg.cell.length < 2) continue;
    const col = Number(rg.cell[0]);
    const row = Number(rg.cell[1]);
    const id = String(rg.id);
    byCell.set(`${col},${row}`, id);
    const g = Number(rg.grid ?? 0);
    const list = byGrid.get(g) ?? [];
    list.push({ id, col, row });
    byGrid.set(g, list);
  }
  const shapeW = Math.max(...relative.map((c) => c[0]));
  const shapeH = Math.max(...relative.map((c) => c[1]));
  const placements = [];
  for (const [grid, gridCells] of [...byGrid.entries()].sort((a, b) => a[0] - b[0])) {
    const maxCol = Math.max(...gridCells.map((c) => c.col));
    const maxRow = Math.max(...gridCells.map((c) => c.row));
    for (let oy = 0; oy <= maxRow - shapeH; oy++) {
      for (let ox = 0; ox <= maxCol - shapeW; ox++) {
        const ids = [];
        let ok = true;
        for (const [dx, dy] of relative) {
          const hit = gridCells.find((c) => c.col === ox + dx && c.row === oy + dy);
          if (!hit) {
            ok = false;
            break;
          }
          ids.push(hit.id);
        }
        if (ok) placements.push({ ox, oy, grid, ids });
      }
    }
  }
  const authored = cells.map(([c, r]) => byCell.get(`${c},${r}`)).filter(Boolean);
  if (authored.length === cells.length) {
    const leftmostOx = placements.reduce((m, p) => Math.min(m, p.ox), Infinity);
    if (Number.isFinite(leftmostOx) && minCol === leftmostOx) {
      const rightmost = placements.reduce((best, p) => {
        if (!best) return p;
        if (p.ox > best.ox) return p;
        if (p.ox === best.ox && (p.oy < best.oy || (p.oy === best.oy && p.grid < best.grid))) return p;
        return best;
      }, null);
      if (rightmost && rightmost.ox > minCol) return rightmost.ids;
    }
    return authored;
  }
  return placements[0] ? placements[0].ids : [];
};

const shadeRevealIds = (spec) => {
  // Multi-group ("complete the pie chart"): the answer is an ASSIGNMENT of region -> category, so the
  // reveal is [{id, group}, …] rather than a flat id list. Lay each category out as one contiguous run
  // over the regions the student was free to colour (`given` ones are the worked example already in
  // the picture), in spec order — that reproduces the mark scheme's sectors. Grading accepts any
  // contiguous run of the right size, so this is one valid solution, drawn with the same per-category
  // colours the student worked in.
  if (Array.isArray(spec.groups) && spec.groups.length) {
    const regions = spec.regions || [];
    const free = regions.filter((r) => !r.given);
    const taken = new Set();
    const out = [];
    for (const g of spec.groups) {
      const n = Number(g.count) || 0;
      const key = String(g.key || "").toLowerCase();
      let picked = 0;
      for (const r of free) {
        if (picked >= n) break;
        const id = String(r.id);
        if (taken.has(id)) continue;
        const accepts = Array.isArray(r.accepts) ? r.accepts.map((a) => String(a).toLowerCase()) : [];
        if (accepts.length && !accepts.includes(key)) continue;
        taken.add(id);
        out.push({ id, group: String(g.key) });
        picked++;
      }
      for (const r of free) {
        if (picked >= n) break;
        const id = String(r.id);
        if (taken.has(id)) continue;
        taken.add(id);
        out.push({ id, group: String(g.key) });
        picked++;
      }
    }
    return out;
  }
  if (Array.isArray(spec.target_regions) && spec.target_regions.length) return spec.target_regions.map(String);
  // "any one of several arrangements" (target_sets) — reveal the first accepted set as one valid example.
  if (Array.isArray(spec.target_sets) && spec.target_sets.length) return (spec.target_sets[0] || []).map(String);
  // count mode ("shade any N") carries no fixed target set — reveal the first N regions as one valid example.
  if (spec.target_mode === "count" && spec.target_count > 0) {
    return (spec.regions || []).slice(0, spec.target_count).map((r) => String(r.id));
  }
  // polyomino ("draw pattern N") — same placement as mobile: authored cells, but a relative
  // shape stored at the grid origin slides to the rightmost fit (empty pattern-N column).
  if (spec.target_mode === "polyomino" && Array.isArray(spec.target_shape)) {
    return shadePolyominoRevealIds(spec);
  }
  // metrics ("any shape with this area/perimeter") — reveal one valid example: the first w×h
  // rectangle on the grid whose cell count and boundary match the targets.
  if (spec.target_mode === "metrics") return metricsRevealExample(spec);
  // ways ("show ALL the ways" on several printed grid copies) — reveal way i drawn on copy i.
  if (spec.target_mode === "ways" && Array.isArray(spec.target_ways)) {
    const byGridCell = new Map(
      (spec.regions || []).map((r) => [`${r.grid ?? 0}|${r.cell?.[0]},${r.cell?.[1]}`, String(r.id)])
    );
    return spec.target_ways.flatMap((way, g) =>
      (way || []).map(([c, r]) => byGridCell.get(`${g}|${c},${r}`)).filter(Boolean)
    );
  }
  return [];
};

const revealValueForKind = (spec) => {
  if (spec.kind === "lines" || spec.kind === "tessellation-grid") return spec.target_lines || [];
  // cut-drag: reveal the canonical cut with the left piece slid by the shape's base vector — the
  // assembled target shape.
  if (spec.kind === "cut-drag") {
    const s = Array.isArray(spec.shape) ? spec.shape : [];
    if (!Array.isArray(spec.target_cut) || s.length < 4) return null;
    const b = [Number(s[2][0]) - Number(s[3][0]), Number(s[2][1]) - Number(s[3][1])];
    return { cut: spec.target_cut, offsets: [b, [0, 0]] };
  }
  if (spec.kind === "segments") {
    const nodes = Array.isArray(spec.nodes) ? spec.nodes : [];
    const segs = (Array.isArray(spec.target_segment_sets) && spec.target_segment_sets[0])
      ? spec.target_segment_sets[0]
      : (spec.target_segments || []);
    return segs
      .map(([i, j]) => (nodes[i] && nodes[j] ? [nodes[i], nodes[j]] : null))
      .filter(Boolean);
  }
  if (spec.kind === "pictogram") {
    const count = Math.max(0, Math.round(Number(spec.row?.count) || 0));
    const t = Number(spec.target_icons) || 0;
    const full = Math.floor(t + 1e-9), frac = Math.round((t - full) * 4) / 4;
    return Array.from({ length: count }, (_, i) => (i < full ? 1 : i === full ? frac : 0));
  }
  if (spec.kind === "tally") {
    if (Array.isArray(spec.cells) && spec.cells.length > 0) {
      return spec.cells.map((c) => Number(c?.target_marks) || 0);
    }
    return [Number(spec.target_marks ?? spec.target_icons) || 0];
  }
  if (spec.kind === "glyph") return [...(spec.targets || []), ...(spec.optional_targets || [])];
  if (spec.kind === "clock") return clockRevealHands(spec);
  // An `angle` answer is a NUMBER OF DEGREES, not a stored pair of arms — swing the target angle off
  // the printed arm (or off due-east when the student draws both) so the reveal shows the angle the
  // grader actually measures rather than an empty figure. y grows downward, hence the negated sine.
  if (spec.kind === "angle") return angleRevealArms(spec);
  // A marker answer is normally a VALUE on the axis (target_value, e.g. 0.5 on a 0..1 probability scale),
  // not a literal point — target_point is null on those. Revealing target_point alone drew an EMPTY scale
  // for the "Correct solution". Project the value back onto the axis to get the point to show.
  if (spec.kind === "marker") {
    const axis = spec.axis;
    const hasAxis = Array.isArray(axis) && axis.length >= 2 && Array.isArray(axis[0]) && Array.isArray(axis[1]);
    // Multi-bar ("complete the bar chart"): each column's own target height, at that column's x.
    if (hasAxis && Array.isArray(spec.bars) && spec.bars.length) {
      const v0 = Number(spec.v0 ?? 0);
      const v1 = Number(spec.v1 ?? 1);
      const span = v1 - v0;
      const [a, b] = axis;
      return spec.bars
        .filter((bar) => bar && Number.isFinite(Number(bar.x)) && bar.target_value != null)
        .map((bar) => {
          const t = span === 0 ? 0 : Math.max(0, Math.min(1, (Number(bar.target_value) - v0) / span));
          return [Number(bar.x), a[1] + t * (b[1] - a[1])];
        });
    }
    // `target_value` is what the grader reads (gradeInteractiveMarker projects the tap onto the axis and
    // compares values); `target_point` is only the editor's cached pixel of it. When an author retyped
    // the value without re-clicking the axis the two disagree, and revealing the stale POINT showed a
    // "correct answer" the grader would have marked wrong — St Anselm's 2025 Q23 graded 500 mL but drew
    // the water level at ~250 mL. Derive the point from the value whenever both are present.
    if (Array.isArray(spec.target_point) && (!hasAxis || spec.target_value == null)) return [spec.target_point];
    if (!hasAxis || spec.target_value == null) return [];
    const v0 = Number(spec.v0 ?? 0);
    const v1 = Number(spec.v1 ?? 1);
    const span = v1 - v0;
    const t = span === 0 ? 0 : Math.max(0, Math.min(1, (Number(spec.target_value) - v0) / span));
    const [a, b] = axis;
    const pivot = Array.isArray(spec.pivot) && spec.pivot.length >= 2 ? spec.pivot : null;
    // A shallow printed gauge is not centred on the needle hub, so the author gives a third point on
    // the curve (`arc`, the middle tick) and gradeInteractiveMarkerDial fits the circle through the
    // three ticks. Revealing on the HUB-centred circle instead put the model arrow on a different arc
    // from the one the grader measures — St Albans 2017 Q24c graded 3.2 litres and drew 3.3. Mirrors
    // gradeInteractiveMarkerDial exactly, including working in aspect-scaled x.
    const hasVia = Array.isArray(spec.arc) && spec.arc.length >= 2;
    if (hasVia || ((spec.style === "dial" || pivot) && pivot)) {
      const aspect = Number(spec.aspect) > 0 ? Number(spec.aspect) : 1;
      const A = [Number(a[0]) * aspect, Number(a[1])];
      const B = [Number(b[0]) * aspect, Number(b[1])];
      const V = hasVia ? [Number(spec.arc[0]) * aspect, Number(spec.arc[1])] : null;
      let P;
      if (hasVia) {
        const det = 2 * (A[0] * (V[1] - B[1]) + V[0] * (B[1] - A[1]) + B[0] * (A[1] - V[1]));
        if (Math.abs(det) < 1e-9) return [];
        const sA = A[0] * A[0] + A[1] * A[1];
        const sV = V[0] * V[0] + V[1] * V[1];
        const sB = B[0] * B[0] + B[1] * B[1];
        P = [
          (sA * (V[1] - B[1]) + sV * (B[1] - A[1]) + sB * (A[1] - V[1])) / det,
          (sA * (B[0] - V[0]) + sV * (A[0] - B[0]) + sB * (V[0] - A[0])) / det,
        ];
      } else {
        P = [Number(pivot[0]) * aspect, Number(pivot[1])];
      }
      const delta = (from, to) => {
        let d = to - from;
        while (d > Math.PI) d -= 2 * Math.PI;
        while (d <= -Math.PI) d += 2 * Math.PI;
        return d;
      };
      const ang0 = Math.atan2(A[1] - P[1], A[0] - P[0]);
      let span = delta(ang0, Math.atan2(B[1] - P[1], B[0] - P[0]));
      if (hasVia) {
        // The middle tick says which way round the circle the scale runs.
        const mid = delta(ang0, Math.atan2(V[1] - P[1], V[0] - P[0]));
        if (mid === 0 || (mid < 0) !== (span < 0) || Math.abs(mid) > Math.abs(span)) {
          span -= (span < 0 ? -1 : 1) * 2 * Math.PI;
        }
      }
      const ang = ang0 + t * span;
      const r = Math.hypot(A[0] - P[0], A[1] - P[1]) || 0.4;
      return [[(P[0] + r * Math.cos(ang)) / aspect, P[1] + r * Math.sin(ang)]];
    }
    return [[a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]];
  }
  // The model drawing names every line, including the ones inside the outline
  // ("show how the pieces fit"). A hull of target_points drops those segments.
  if (spec.kind === "points" || spec.kind === "polygon") {
    const modelKeys = edgeKeysFrom(spec.model_edges);
    if (modelKeys.length > 0) {
      const out = { points: pointsWithEdgeVertices(spec.target_points || [], modelKeys), edges: modelKeys };
      if (spec.require_shape_label && typeof spec.shape_label === "string" && spec.shape_label.trim() !== "") {
        out.shape_label = spec.shape_label.trim();
      }
      return out;
    }
  }

  // Open lines anchored to a GIVEN point (e.g. "join K to L" where K is context_points, never plotted):
  // the plain target_points list the generic fallback below reveals doesn't include K at all, so the
  // canvas's own point-to-point auto-join (there being no drawn edges to show instead) chains whatever
  // IS revealed in array order — wrongly drawing L to the reflected line's first point instead of K to L,
  // and never drawing K to L at all. required_edges already names the exact lines to draw, so reveal
  // them as real edges (same "gx,gy|gx,gy" keys InteractiveDiagramCanvas.jsx's edgeKey() produces).
  // Also closed "complete the shape" figures whose outline is partly PRINTED (given_points + the
  // reflected corner): the model answer is the corner plus exactly the joining edges, never a full ring.
  // A closed polygon stores the same list: the outline plus any extra segments the author drew.
  if (
    (spec.kind === "points" || spec.kind === "polygon")
    && Array.isArray(spec.required_edges) && spec.required_edges.length > 0
  ) {
    const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const edgeKey = (a, b) => [cornerKey(a), cornerKey(b)].sort().join("|");
    const edges = edgeKeysFrom(spec.required_edges);
    // required_edges names only the sides THIS part has to add. On a closed figure whose other sides
    // were drawn by an earlier part ("join A to B and A to C", then "mark corner D"), revealing those
    // alone showed two loose lines where the model answer is a rectangle — James Allen's 2025 Q12(c).
    // `closed: true` says the figure is a ring, so complete it round the printed and plotted corners.
    //
    // But `closed` is also set on figures that are NOT one cycle: "draw another triangle joined onto
    // this one" is two triangles meeting at a single vertex, and a ring through all five corners is a
    // pentagon the question never asks for. The ring is only the real outline when every edge the
    // student had to draw lies ON it — that test tells the two apart exactly.
    if (spec.closed === true) {
      const ring = orderClosedBoundary([
        ...(Array.isArray(spec.context_points) ? spec.context_points : []),
        ...(Array.isArray(spec.given_points) ? spec.given_points : []),
        ...(Array.isArray(spec.target_points) ? spec.target_points : []),
      ]);
      if (ring.length >= 3) {
        const ringEdges = ring.map((p, i) => edgeKey(p, ring[(i + 1) % ring.length]));
        if (edges.every((k) => ringEdges.includes(k))) {
          ringEdges.forEach((k) => {
            if (!edges.includes(k)) edges.push(k);
          });
        }
      }
    }
    const out = { points: spec.target_points || [], edges };
    if (spec.require_shape_label && typeof spec.shape_label === "string" && spec.shape_label.trim() !== "") {
      out.shape_label = spec.shape_label.trim();
    }
    return out;
  }
  // "Plot D and draw the parallelogram ABCD": A/B/C are given (context_points) and only D is plotted, so
  // target_points is just D — revealing that alone shows a lone dot and no shape. Return explicit edges
  // (not a flat 4-point list) so A/B/C stay printed grey marks and the outline still draws — including
  // when the spec is authored open (closed/connect false) so the student traces the sides by hand.
  let pts = spec.target_points || [];
  // Reflect/rotate: the model answer is the mirrored/rotated original, not a stale target_points
  // hull (dual-grid reviews were drawing one giant green quad instead of the reflected hooks).
  if (spec.kind === "reflect" || spec.kind === "rotate") {
    const authored = Array.isArray(spec.target_points)
      ? spec.target_points.filter((p) => Array.isArray(p) && p.length >= 2 && !Array.isArray(p[0]))
      : [];
    const gridMeta = spec.grid || {};
    const cols = Math.max(1, Math.round(Number(gridMeta.cols ?? 8)));
    const rows = Math.max(1, Math.round(Number(gridMeta.rows ?? 8)));
    const hullKeys = [`0,0`, `${cols},0`, `0,${rows}`, `${cols},${rows}`];
    const isGridHull =
      authored.length === 4 &&
      hullKeys.every((key) =>
        authored.some((p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}` === key)
      );
    const multiShape = Array.isArray(spec.original_shapes) && spec.original_shapes.length > 1;
    const original = Array.isArray(spec.original_shape) ? spec.original_shape.filter((p) => Array.isArray(p) && p.length >= 2) : [];
    const grid = spec.grid || {};
    const x0 = Number(grid.x0 ?? 0.1), y0 = Number(grid.y0 ?? 0.1);
    const dx = Number(grid.dx ?? 0.1) || 0.1, dy = Number(grid.dy ?? 0.1) || 0.1;
    const toGrid = (p) => [(Number(p[0]) - x0) / dx, (Number(p[1]) - y0) / dy];
    let computed = [];
    if (spec.kind === "reflect" && Array.isArray(spec.mirror) && spec.mirror.length >= 2 && original.length) {
      const a = toGrid(spec.mirror[0]), b = toGrid(spec.mirror[1]);
      const ldx = b[0] - a[0], ldy = b[1] - a[1], dd = ldx * ldx + ldy * ldy || 1;
      computed = original.map((p) => {
        const px = Number(p[0]), py = Number(p[1]);
        const t = ((px - a[0]) * ldx + (py - a[1]) * ldy) / dd;
        return [Math.round(2 * (a[0] + t * ldx) - px), Math.round(2 * (a[1] + t * ldy) - py)];
      });
    } else if (spec.kind === "rotate" && Array.isArray(spec.center) && spec.center.length >= 2 && original.length) {
      const c = toGrid(spec.center);
      const ang = spec.angle || "cw90";
      computed = original.map((p) => {
        const dxp = Number(p[0]) - c[0], dyp = Number(p[1]) - c[1];
        const [nx, ny] = ang === "cw90" ? [-dyp, dxp] : ang === "ccw90" ? [dyp, -dxp] : [-dxp, -dyp];
        return [Math.round(c[0] + nx), Math.round(c[1] + ny)];
      });
    }
    const pointKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const multiset = (list) => list.map(pointKey).sort().join("|");
    // Same corners as the printed shape's reflection, stored in another order, draw a
    // different polygon. Join them the way the original is joined. A hand-moved outline
    // (different coordinates) keeps the saved order. A four-corner grid hull is not that polygon.
    if (
      !isGridHull
      && !multiShape
      && authored.length >= 3
      && computed.length >= 3
      && multiset(authored) === multiset(computed)
    ) {
      const labels = new Map();
      authored.forEach((p) => {
        if (typeof p[2] === "string" && p[2].trim()) labels.set(pointKey(p), p[2].trim());
      });
      return computed.map((p) => {
        const label = labels.get(pointKey(p));
        return label ? [p[0], p[1], label] : p;
      });
    }
    if (!isGridHull && !multiShape && authored.length >= 3) {
      return authored.map((p) => [Number(p[0]), Number(p[1])]);
    }
    return computed.length ? computed : pts;
  }
  const printedPts = [
    ...(Array.isArray(spec.context_points) ? spec.context_points : []),
    ...(Array.isArray(spec.given_points) ? spec.given_points : []),
  ];
  // "Draw a diagram to show how the 4 pieces fit together": the answer is the outline AND the lines where
  // the pieces meet. The reveal used to draw only the outline from target_points — the very answer that
  // had just been marked partial (NLIGSC G2 2013 Q29) — so the model lines travel as model_edges.
  if ((spec.kind === "polygon" || spec.kind === "points") && Array.isArray(spec.model_edges) && spec.model_edges.length) {
    const key = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const edges = spec.model_edges
      .map((e) => (typeof e === "string" ? e : (Array.isArray(e) && e.length === 2 ? [key(e[0]), key(e[1])].sort().join("|") : null)))
      .filter(Boolean);
    const corners = new Map();
    edges.forEach((e) => e.split("|").forEach((k) => corners.set(k, k.split(",").map(Number))));
    return { points: [...corners.values()], edges };
  }
  // "Show how the two triangles fit together": the outline plus the seam between them (required_chords,
  // index pairs into target_points). The bare corner list revealed a plain rectangle (NLIGSC G1 2016 Q36).
  if (spec.kind === "points" && Array.isArray(spec.required_chords) && spec.required_chords.length
    && Array.isArray(pts) && pts.length >= 3 && printedPts.length === 0) {
    const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const ring = pts.map((p, i) => [cornerKey(p), cornerKey(pts[(i + 1) % pts.length])].sort().join("|"));
    const chords = spec.required_chords
      .filter((c) => Array.isArray(c) && pts[c[0]] && pts[c[1]])
      .map(([i, j]) => [cornerKey(pts[i]), cornerKey(pts[j])].sort().join("|"));
    return { points: pts, edges: [...ring, ...chords] };
  }
  const closedConnect = spec.closed !== false && spec.connect !== false;
  const missingVertex = printedPts.length >= 3 && Array.isArray(spec.target_points) && spec.target_points.length >= 1;
  if (
    (spec.kind === "points" || spec.kind === "polygon")
    && printedPts.length > 0
    && Array.isArray(spec.target_points)
    && printedPts.length + spec.target_points.length >= 3
    && (spec.join_context_edges === true || closedConnect || missingVertex)
  ) {
    const ring = orderClosedBoundary([...printedPts, ...spec.target_points]);
    const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const edges = ring.map((p, i) => [cornerKey(p), cornerKey(ring[(i + 1) % ring.length])].sort().join("|"));
    const out = { points: spec.target_points, edges };
    if (spec.require_shape_label && typeof spec.shape_label === "string" && spec.shape_label.trim() !== "") {
      out.shape_label = spec.shape_label.trim();
    }
    return out;
  }
  // "Label the triangle A" / "Label your shape B": reveal carries the shape letter on the value object so
  // the canvas can paint it at the centroid (same channel the student answer uses).
  if (spec.require_shape_label && typeof spec.shape_label === "string" && spec.shape_label.trim() !== "") {
    return { points: pts, shape_label: spec.shape_label.trim() };
  }
  // A closed polygon with no printed corners used to return a bare point list. The canvas then
  // drew tap-order segments and skipped the closing edge, so a 3×1 rectangle (NLIGSC 2013 Q29a)
  // revealed as a horizontal bar with two short ticks instead of the finished shape.
  if (spec.kind === "polygon" && Array.isArray(pts) && pts.length >= 3 && spec.closed !== false) {
    const cornerKey = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    const edges = pts.map((p, i) => [cornerKey(p), cornerKey(pts[(i + 1) % pts.length])].sort().join("|"));
    return { points: pts, edges };
  }
  return pts;
};
